import React, { useState, useEffect } from 'react';
import {
    Card, Button, Table, Modal, Input,
    Switch, Tag, message, Row, Col, Alert, Tooltip
} from 'antd';
import {
    PlusOutlined, CheckCircleFilled, CalendarOutlined,
DeleteOutlined, StarFilled, EditOutlined
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import { AntdDatepicker } from '../../utils/Antd';
import moment from 'moment';
import { trailCreate, trailUpdate, trailDelete, TRAIL_MODULES } from '../../utils/activityTrail';

export default function QoCPeriods() {
    const [periods, setPeriods]     = useState([]);
    const [loading, setLoading]     = useState(false);
    const [modalOpen, setModal]     = useState(false);
    const [editing, setEditing]     = useState(null);
    const [form, setForm]           = useState({});

    useEffect(() => { load(); }, []);

    const load = async () => {
        setLoading(true);
        try {
            const r = await QoCService.getPeriods();
            setPeriods(r.data?.data || []);
        } catch { message.error('Failed to load periods'); }
        finally { setLoading(false); }
    };

    const openNew = () => {
        setEditing(null);
        setForm({ is_active: 1, is_current: 0 });
        setModal(true);
    };

    const openEdit = (p) => {
        setEditing(p);
        setForm({
            ...p,
            period_start: p.period_start ? moment(p.period_start) : null,
            period_end:   p.period_end   ? moment(p.period_end)   : null,
        });
        setModal(true);
    };

    const handleSave = async () => {
        if (!form.label || !form.period_start || !form.period_end) {
            message.error('Label, Start Date and End Date are required');
            return;
        }
        setLoading(true);
        try {
            await QoCService.savePeriod({
                ...form,
                period_start: moment(form.period_start).format('YYYY-MM-DD'),
                period_end:   moment(form.period_end).format('YYYY-MM-DD'),
                is_active:    form.is_active ? 1 : 0,
                is_current:   form.is_current ? 1 : 0,
            });

            editing
                ? trailUpdate(TRAIL_MODULES.PERIODS, form.label, editing.id, {
                    start: moment(form.period_start).format('YYYY-MM-DD'),
                    end:   moment(form.period_end).format('YYYY-MM-DD'),
                    is_current: form.is_current ? 1 : 0,
                  })
                : trailCreate(TRAIL_MODULES.PERIODS, form.label, '', {
                    start: moment(form.period_start).format('YYYY-MM-DD'),
                    end:   moment(form.period_end).format('YYYY-MM-DD'),
                  });
            message.success('Period saved');
            setModal(false);
            load();
        } catch { message.error('Failed to save'); }
        finally { setLoading(false); }
    };

    const handleSetCurrent = async (id) => {
        try {
            await QoCService.setCurrentPeriod(id);

            const period = periods.find(p => p.id === id);
            trailUpdate(TRAIL_MODULES.PERIODS, period?.label || String(id), id,
                { action: 'Set as current period' });
            message.success('Current period updated — all modules will now default to this period');
            load();
        } catch { message.error('Failed to update'); }
    };

    const handleDelete = async (id) => {
        Modal.confirm({
            title: 'Delete this period?',
            content: 'This will not delete assessments or KPI data linked to this period label.',
            okType: 'danger',
            onOk: async () => {
                const period = periods.find(p => p.id === id);
                await QoCService.deletePeriod(id);
                trailDelete(TRAIL_MODULES.PERIODS, period?.label || String(id), id);
                message.success('Deleted');
                load();
            }
        });
    };

    const current = periods.find(p => parseInt(p.is_current) === 1);

    const columns = [
        {
            title: '', width: 40,
            render: (_, p) => parseInt(p.is_current) === 1
                ? <Tooltip title="Current active period"><StarFilled style={{ color: '#faad14', fontSize: 16 }}/></Tooltip>
                : null
        },
        {
            title: 'Period Label', dataIndex: 'label', width: 160,
            render: (label, p) => (
                <div>
                    <span style={{ fontWeight: 800, fontSize: 14,
                        color: parseInt(p.is_current) === 1 ? '#1890ff' : '#1a1a2e' }}>
                        {label}
                    </span>
                    {parseInt(p.is_current) === 1 && (
                        <Tag color="blue" style={{ marginLeft: 8, fontSize: 10 }}>CURRENT</Tag>
                    )}
                </div>
            )
        },
        {
            title: 'Start Date', dataIndex: 'period_start', width: 120,
            render: d => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {moment(d).format('DD MMM YYYY')}
            </span>
        },
        {
            title: 'End Date', dataIndex: 'period_end', width: 120,
            render: d => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                {moment(d).format('DD MMM YYYY')}
            </span>
        },
        {
            title: 'Duration', width: 100,
            render: (_, p) => {
                const months = moment(p.period_end).diff(moment(p.period_start), 'months');
                return <span style={{ color: '#888', fontSize: 12 }}>{months} months</span>;
            }
        },
        {
            title: 'Status', width: 100,
            render: (_, p) => {
                const now = moment();
                const started = now.isAfter(moment(p.period_start));
                const ended   = now.isAfter(moment(p.period_end));
                if (ended)    return <Tag color="default">Ended</Tag>;
                if (started)  return <Tag color="green">Active</Tag>;
                return <Tag color="blue">Upcoming</Tag>;
            }
        },
        {
            title: 'Visible', dataIndex: 'is_active', width: 80,
            render: (v, p) => (
                <Switch size="small" checked={v == 1}
                    onChange={async (checked) => {
                        await QoCService.savePeriod({ ...p, is_active: checked ? 1 : 0,
                            period_start: p.period_start, period_end: p.period_end });
                        load();
                    }}/>
            )
        },
        {
            title: 'Description', dataIndex: 'description', ellipsis: true,
            render: d => <span style={{ fontSize: 11, color: '#888' }}>{d || '—'}</span>
        },
        {
            title: '', width: 180,
            render: (_, p) => (
                <div style={{ display: 'flex', gap: 6 }}>
                    {parseInt(p.is_current) !== 1 && (
                        <Button size="small" type="primary" ghost
                            icon={<StarFilled/>}
                            onClick={() => handleSetCurrent(p.id)}
                            style={{ fontSize: 11 }}>
                            Set Current
                        </Button>
                    )}
                    <Button size="small" icon={<EditOutlined/>} onClick={() => openEdit(p)}/>
                    <Button size="small" danger icon={<DeleteOutlined/>} onClick={() => handleDelete(p.id)}/>
                </div>
            )
        },
    ];

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>Assessment Periods</h2>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 2 }}>
                        Define assessment cycles — the active period flows across all modules
                    </div>
                </div>
                <div className="my-auto ml-auto">
                    <Button type="primary" icon={<PlusOutlined/>} onClick={openNew}>
                        New Period
                    </Button>
                </div>
            </div>

            <div className="page-pad">

                {/* Current period banner */}
                {current ? (
                    <div style={{
                        background: 'linear-gradient(135deg,#e6f4ff,#f0f7ff)',
                        border: '2px solid #1890ff40',
                        borderRadius: 12,
                        padding: '16px 24px',
                        marginBottom: 20,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 20,
                    }}>
                        <StarFilled style={{ fontSize: 28, color: '#faad14' }}/>
                        <div>
                            <div style={{ fontSize: 11, color: '#888', fontWeight: 700,
                                letterSpacing: 1, textTransform: 'uppercase' }}>
                                Current Assessment Period
                            </div>
                            <div style={{ fontSize: 22, fontWeight: 900, color: '#1890ff' }}>
                                {current.label}
                            </div>
                            <div style={{ fontSize: 12, color: '#555', marginTop: 2 }}>
                                <CalendarOutlined style={{ marginRight: 5 }}/>
                                {moment(current.period_start).format('DD MMM YYYY')}
                                {' → '}
                                {moment(current.period_end).format('DD MMM YYYY')}
                                {current.description && (
                                    <span style={{ marginLeft: 12, color: '#888' }}>
                                        — {current.description}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                            <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
                                This period label is used in:
                            </div>
                            {['Audit Schedules', 'KPI Targets', 'Quality Index',
                              'Customer Surveys', 'Executive Dashboard'].map(m => (
                                <Tag key={m} color="blue" style={{ fontSize: 10, margin: 2 }}>{m}</Tag>
                            ))}
                        </div>
                    </div>
                ) : (
                    <Alert
                        message="No current period set"
                        description="Create a period and click 'Set Current' to activate it. All modules will use this period as their default."
                        type="warning" showIcon style={{ marginBottom: 16 }}/>
                )}

                {/* How it works */}
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="How periods work across the platform"
                    description={
                        <ul style={{ margin: '4px 0 0', paddingLeft: 20, fontSize: 12 }}>
                            <li><b>Audit Schedules</b> — when creating schedules, the current period label is pre-filled</li>
                            <li><b>KPI Targets</b> — period list comes from here; targets are set per period</li>
                            <li><b>Quality Index</b> — calculation uses the period's start/end dates to pull assessments and surveys</li>
                            <li><b>Customer Surveys</b> — period label is pre-filled from the current period</li>
                            <li><b>Executive Dashboard</b> — Period filter dropdown is populated from this list</li>
                            <li><b>Compliance Analytics</b> — date ranges default to current period start/end</li>
                        </ul>
                    }/>

                <Card size="small">
                    <Table
                        columns={columns}
                        dataSource={periods}
                        rowKey="id"
                        loading={loading}
                        pagination={false}
                        rowClassName={p => parseInt(p.is_current) === 1 ? 'ant-table-row-selected' : ''}/>
                </Card>
            </div>

            {/* Save modal */}
            <Modal
                title={editing ? `Edit Period — ${editing.label}` : 'New Assessment Period'}
                open={modalOpen}
                onCancel={() => setModal(false)}
                onOk={handleSave}
                confirmLoading={loading}
                okText="Save Period"
                width={560}>
                <Row gutter={[16, 12]} style={{ marginTop: 8 }}>
                    <Col span={24}>
                        <label style={{ fontWeight: 700, display: 'block', marginBottom: 4 }}>
                            Period Label * <span style={{ fontWeight: 400, color: '#888', fontSize: 11 }}>
                                (used everywhere — keep it consistent, e.g. "FY2025/26")
                            </span>
                        </label>
                        <Input
                            value={form.label}
                            onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                            placeholder="e.g. FY2025/26 or FY2025/26 Q1"
                            size="large"
                            style={{ fontWeight: 700, fontSize: 15 }}/>
                    </Col>
                    <Col span={12}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Start Date *</label>
                        <AntdDatepicker
                            value={form.period_start}
                            onChange={v => setForm(f => ({ ...f, period_start: v }))}
                            style={{ width: '100%' }}/>
                    </Col>
                    <Col span={12}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>End Date *</label>
                        <AntdDatepicker
                            value={form.period_end}
                            onChange={v => setForm(f => ({ ...f, period_end: v }))}
                            style={{ width: '100%' }}/>
                    </Col>
                    <Col span={24}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 4 }}>Description</label>
                        <Input
                            value={form.description}
                            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                            placeholder="e.g. Annual QoC audit cycle for financial year 2025/26"/>
                    </Col>
                    <Col span={12}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
                            Visible in dropdowns
                        </label>
                        <Switch
                            checked={form.is_active == 1}
                            onChange={v => setForm(f => ({ ...f, is_active: v ? 1 : 0 }))}
                            checkedChildren="Visible" unCheckedChildren="Hidden"/>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                            Hide old periods without deleting them
                        </div>
                    </Col>
                    <Col span={12}>
                        <label style={{ fontWeight: 600, display: 'block', marginBottom: 6 }}>
                            Set as current period
                        </label>
                        <Switch
                            checked={form.is_current == 1}
                            onChange={v => setForm(f => ({ ...f, is_current: v ? 1 : 0 }))}
                            checkedChildren="Current" unCheckedChildren="Not current"/>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                            Only one period can be current at a time
                        </div>
                    </Col>
                </Row>
            </Modal>
        </div>
    );
}
